/**
 * Pre-payment summary of a merchant order. Every amount comes from the server
 * (create-order, quote_only) in integer cents with the exact currency sent to Stripe;
 * the browser never computes, adds or rounds any price, tax or shipping fee.
 */
export interface OrderQuoteLine {
  product_id: string;
  quantity: number;
  unit_cents: number;
  line_cents: number;
}

export interface OrderQuote {
  currency: string;
  subtotal_cents: number;
  shipping_cents: number;
  tax_cents: number;
  total_cents: number;
  shipping_configured: boolean;
  shipping_free?: boolean;
  shipping_method: { id: string; name: string } | null;
  tax_included_unconfigured?: boolean;
  lines: OrderQuoteLine[];
}

const isCents = (v: unknown) => typeof v === "number" && Number.isInteger(v) && v >= 0;

/** Accepts only a complete, well-formed server quote; anything else is treated as no quote. */
export function parseOrderQuote(value: unknown): OrderQuote | null {
  const q = value as OrderQuote | null;
  if (!q || typeof q !== "object") return null;
  if (typeof q.currency !== "string" || !/^[A-Z]{3}$/.test(q.currency)) return null;
  if (![q.subtotal_cents, q.shipping_cents, q.tax_cents, q.total_cents].every(isCents)) return null;
  if (q.subtotal_cents + q.shipping_cents + q.tax_cents !== q.total_cents) return null;
  if (!Array.isArray(q.lines) || q.lines.some((l) => !isCents(l?.unit_cents) || !isCents(l?.line_cents))) return null;
  return q;
}

/** Formats server cents in the server currency (fr-FR), e.g. 1000 + "EUR" -> "10,00 €". */
export function formatCents(cents: number, currency: string): string {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency }).format(cents / 100);
}
